/**
 * Edit Hardware Modal - Full Screen
 * Shows complete phone configuration with tabs for accounts, line keys, and expansion
 * Stays on same page to preserve customer context
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { hardwareService } from '../../services/hardwareService';
import type { Hardware } from './types';
import ButtonConfigurator from './ButtonConfigurator';
import PhoneAccounts from './PhoneAccounts';
import VisualPhoneLayout from './VisualPhoneLayout';

interface EditHardwareModalProps {
  open: boolean;
  onClose: () => void;
  hardwareId: number;
  customerId: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const EditHardwareModal: React.FC<EditHardwareModalProps> = ({
  open,
  onClose,
  hardwareId,
  customerId,
}) => {
  const [hardware, setHardware] = useState<Hardware | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const [selectedButton, setSelectedButton] = useState<number | null>(null);
  const [isExpansion, setIsExpansion] = useState(false);

  // Scroll position preservation
  const scrollRef = useRef<HTMLDivElement>(null);
  const savedScrollPosition = useRef<number>(0);

  useEffect(() => {
    if (open && hardwareId) {
      loadHardware();
    }
  }, [open, hardwareId, customerId]);

  // Restore scroll position after hardware loads
  useEffect(() => {
    if (!loading && hardware && scrollRef.current && savedScrollPosition.current > 0) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = savedScrollPosition.current;
        }
      }, 100);
    }
  }, [loading, hardware]);

  const loadHardware = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[EditHardwareModal] Loading hardware:', { hardwareId, customerId });
      const data = await hardwareService.getById(hardwareId, customerId);
      setHardware(data);
    } catch (err: any) {
      console.error('[EditHardwareModal] Failed to load hardware:', err);

      // If 404, hardware was likely deleted - close the modal
      if (err.response?.status === 404) {
        console.log('[EditHardwareModal] Hardware not found (404) - closing modal');
        onClose();
        return;
      }

      setError(err.message || 'Failed to load hardware');
    } finally {
      setLoading(false);
    }
  };

  const formatMac = (mac: string) => {
    return mac.match(/.{1,2}/g)?.join(':').toUpperCase() || mac;
  };

  const handleButtonClick = (buttonNo: number | null, expansion: boolean = false) => {
    setSelectedButton(buttonNo);
    setIsExpansion(expansion);
    setConfiguratorOpen(true);
  };

  const handleConfiguratorClose = () => {
    // Save current scroll position before reloading
    if (scrollRef.current) {
      savedScrollPosition.current = scrollRef.current.scrollTop;
    }
    setConfiguratorOpen(false);
    setSelectedButton(null);
    setIsExpansion(false);
    loadHardware(); // Reload to get updated config
  };

  const getConfiguredKeys = (expansion: boolean): number[] => {
    if (expansion) {
      return hardware?.expansionKeys?.map(k => k.KeyNo) || [];
    } else {
      return hardware?.lineKeys?.map(k => k.LineKeyNo) || [];
    }
  };

  const getLineKeyConfig = (buttonNo: number) => {
    return hardware?.lineKeys?.find(lk => lk.LineKeyNo === buttonNo);
  };

  const getExpansionKeyConfig = (buttonNo: number) => {
    return hardware?.expansionKeys?.find(ek => ek.KeyNo === buttonNo);
  };

  const renderLineKeysTable = (expansion: boolean = false) => {
    const keys = expansion ? hardware?.expansionKeys || [] : hardware?.lineKeys || [];

    if (keys.length === 0) {
      return (
        <Alert severity="info">
          No line keys configured yet. Click "Add Line Key" to get started.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Line #</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((key) => {
              const isExpansion = 'expansion_module.key.type' in key;
              const keyNo = isExpansion ? key.KeyNo : key.LineKeyNo;
              const type = isExpansion ? key['expansion_module.key.type'] : key['linekey.type'];
              const label = isExpansion ? key['expansion_module.key.label'] : key['linekey.label'];
              const typeName = key.typeName || 'Unknown';

              // Display value - show the extension number for all types
              let value = '';
              const typeStr = String(type);

              if (isExpansion) {
                switch (typeStr) {
                  case '13': // Speed Dial
                    value = key['expansion_module.key.value'] || '';
                    break;
                  case '15': // Line - show extension, not line number
                    value = key.Extension ? String(key.Extension) : '';
                    break;
                  case '16': // BLF
                    value = key.Extension ? String(key.Extension) : '';
                    break;
                }
              } else {
                switch (typeStr) {
                  case '13': // Speed Dial
                    value = key['linekey.value'] || '';
                    break;
                  case '15': // Line - show extension, not line number
                    const extNum = Number(key.Extension);
                    value = key.Extension && extNum > 0 ? String(key.Extension) : '';
                    break;
                  case '16': // BLF
                    const blfNum = Number(key.Extension);
                    value = key.Extension && blfNum > 0 ? String(key.Extension) : '';
                    break;
                }
              }

              return (
                <TableRow key={keyNo} hover>
                  <TableCell>{keyNo}</TableCell>
                  <TableCell>
                    <Chip
                      label={typeName}
                      color={type === 13 ? 'primary' : type === 15 ? 'success' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{value || '-'}</TableCell>
                  <TableCell>{label || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleButtonClick(keyNo, expansion)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteKey(keyNo, expansion)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const handleDeleteKey = async (buttonNo: number, expansion: boolean) => {
    if (!window.confirm(`Are you sure you want to delete line key ${buttonNo}?`)) {
      return;
    }

    // Save current scroll position before reloading
    if (scrollRef.current) {
      savedScrollPosition.current = scrollRef.current.scrollTop;
    }

    try {
      await hardwareService.deleteLineKey(hardwareId, buttonNo, expansion, customerId);
      await loadHardware();
    } catch (err) {
      console.error('Failed to delete line key:', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PhoneIcon sx={{ fontSize: 28 }} />
            <Box>
              {hardware && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {hardware.manufacturer} {hardware.model}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    MAC: {hardware.MacAddr ? formatMac(hardware.MacAddr) : 'N/A'} • {hardware.location || 'No location'}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} edge="end" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent ref={scrollRef} sx={{ py: 1, px: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error || !hardware ? (
          <Alert severity="error">{error || 'Hardware not found'}</Alert>
        ) : (
          <>
            {/* Info Cards */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">Serial Number</Typography>
                  <Typography variant="body2" fontWeight="medium">{hardware.SerialNum || 'N/A'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2" fontWeight="medium">{hardware.phoneType || 'N/A'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">Line Keys</Typography>
                  <Typography variant="body2" fontWeight="medium">{hardware.linekeys}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">Admin Password</Typography>
                  <Typography variant="body2" fontFamily="monospace" fontWeight="medium">{hardware.password}</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Paper>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Visual Layout" />
                <Tab label="Phone Accounts" />
                <Tab label={`Line Keys (${hardware.linekeys})`} />
                {!!hardware.extModAvail && <Tab label="Expansion Module (40)" />}
              </Tabs>

              <Divider />

              {/* Tab 0: Visual Layout */}
              <TabPanel value={tabValue} index={0}>
                <VisualPhoneLayout
                  hardware={hardware}
                  onButtonClick={handleButtonClick}
                />
              </TabPanel>

              {/* Tab 1: Phone Accounts */}
              <TabPanel value={tabValue} index={1}>
                <PhoneAccounts hardware={hardware} onUpdate={loadHardware} customerId={customerId} />
              </TabPanel>

              {/* Tab 2: Line Keys */}
              <TabPanel value={tabValue} index={2}>
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => handleButtonClick(null, false)}
                    sx={{ mb: 2 }}
                  >
                    Add Line Key
                  </Button>
                  {renderLineKeysTable(false)}
                </Box>
              </TabPanel>

              {/* Tab 3: Expansion Module */}
              {!!hardware.extModAvail && (
                <TabPanel value={tabValue} index={3}>
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => handleButtonClick(null, true)}
                      sx={{ mb: 2 }}
                    >
                      Add Line Key
                    </Button>
                    {renderLineKeysTable(true)}
                  </Box>
                </TabPanel>
              )}
            </Paper>

            {/* Button Configurator Modal */}
            <ButtonConfigurator
              open={configuratorOpen}
              onClose={handleConfiguratorClose}
              hardwareId={hardware.hardwareID}
              buttonNo={selectedButton}
              isExpansion={isExpansion}
              currentConfig={
                selectedButton
                  ? isExpansion
                    ? getExpansionKeyConfig(selectedButton)
                    : getLineKeyConfig(selectedButton)
                  : undefined
              }
              accounts={hardware.accounts || []}
              totalLineKeys={hardware.linekeys}
              configuredKeys={getConfiguredKeys(isExpansion)}
              customerId={customerId}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditHardwareModal;
